import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuHouse, LuUser } from "react-icons/lu";
import { api, ApiError , errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useActiveFlat } from "../lib/active-flat";
import type { Flat } from "../lib/types";
import { Button, ErrorBanner, Input, Label } from "../components/ui";

export function CreateOrJoinFlatPage() {
  const navigate = useNavigate();
  const { refreshUser, logout } = useAuth();
  const { setFlat, reload } = useActiveFlat();

  const [flatName, setFlatName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);

  const afterFlat = async (flat: Flat) => {
    setFlat(flat);
    await refreshUser();
    await reload();
    navigate("/", { replace: true });
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    const name = flatName.trim();
    if (!name) return setCreateError("Give your flat a name.");
    setCreating(true);
    try {
      const flat = await api.post<Flat>("/flat", { name });
      await afterFlat(flat);
    } catch (err) {
      setCreateError(
        errMsg(err, "Couldn't create your flat. Try again."));
    } finally {
      setCreating(false);
    }
  };

  const join = async (e: React.FormEvent) => {
    e.preventDefault();
    setJoinError(null);
    const code = inviteCode.trim();
    if (!code) return setJoinError("Paste the invite code from your flatmate.");
    setJoining(true);
    try {
      const flat = await api.post<Flat>("/flat/join", { inviteCode: code });
      await afterFlat(flat);
    } catch (err) {
      setJoinError(
        err instanceof ApiError
          ? err.message || "We couldn't find a flat with that code."
          : "Couldn't join — please try again.",
      );
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-brand-50 via-white to-slate-50 px-4 py-12">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold text-slate-900">Set up your flat</h1>
          <p className="mt-1 text-sm text-slate-500">
            Create a new flat for your household, or join one you've been invited to.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4">
              <div className="mb-3 grid h-10 w-10 place-items-center rounded-lg bg-brand-100 text-brand-700">
                <LuHouse className="h-5 w-5" />
              </div>
              <h2 className="text-base font-semibold text-slate-900">Create a flat</h2>
              <p className="text-sm text-slate-500">You'll become the first admin.</p>
            </div>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label htmlFor="flatName">Flat name</Label>
                <Input
                  id="flatName"
                  required
                  placeholder="The Cosy Loft"
                  value={flatName}
                  onChange={(e) => setFlatName(e.target.value)}
                  disabled={creating}
                />
              </div>
              <ErrorBanner message={createError} />
              <Button type="submit" loading={creating} className="w-full">
                Create flat
              </Button>
            </form>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4">
              <div className="mb-3 grid h-10 w-10 place-items-center rounded-lg bg-emerald-100 text-emerald-700">
                <LuUser className="h-5 w-5" />
              </div>
              <h2 className="text-base font-semibold text-slate-900">Join a flat</h2>
              <p className="text-sm text-slate-500">Enter the invite code your flatmate shared.</p>
            </div>
            <form onSubmit={join} className="space-y-4">
              <div>
                <Label htmlFor="invite">Invite code</Label>
                <Input
                  id="invite"
                  required
                  placeholder="abc-123"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  disabled={joining}
                />
              </div>
              <ErrorBanner message={joinError} />
              <Button type="submit" variant="secondary" loading={joining} className="w-full">
                Join flat
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={async () => {
              await logout();
              navigate("/login", { replace: true });
            }}
            className="text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
